import { useId, useState } from "react"

import type { LandingPageHtmlEmbedBlock } from "@forento/core/models/landing"
import { htmlEmbedMaxHeight, htmlEmbedMaxLength, htmlEmbedMinHeight } from "@forento/core/schemas/landing"
import LandingHtmlEmbed from "@forento/ui/components/LandingHtmlEmbed"

import EditorShell from "./EditorShell"

type Props = {
	block: LandingPageHtmlEmbedBlock
	onChange: (block: LandingPageHtmlEmbedBlock) => void
	onClose: () => void
}

export default function HtmlEmbedBlockEditor({ block, onChange, onClose }: Props) {
	const id = useId()
	const [previewHtml, setPreviewHtml] = useState(block.content.html)
	const update = (content: Partial<LandingPageHtmlEmbedBlock["content"]>) =>
		onChange({ type: "html-embed", content: { ...block.content, ...content } })

	return (
		<EditorShell onClose={onClose}>
			<section className="px-6 pt-20 pb-8 sm:pt-24 lg:px-8">
				<div className="mx-auto max-w-3xl space-y-5">
					<div className="space-y-2">
						<label htmlFor={id} className="block font-semibold">
							HTML embed code
						</label>
						<p id={`${id}-help`} className="text-sm text-gray-500">
							Paste the embed code from your newsletter, booking, or widget provider. Use Preview to try
							it. Some providers require cookies or access to your website and may not work in an isolated
							embed.
						</p>
						<textarea
							id={id}
							aria-describedby={`${id}-help`}
							value={block.content.html}
							onChange={event => update({ html: event.target.value })}
							maxLength={htmlEmbedMaxLength}
							spellCheck={false}
							rows={12}
							placeholder='<form action="https://…" method="post">…</form>'
							className="w-full rounded-lg border border-gray-200 bg-white p-4 font-mono text-sm"
						/>
					</div>
					<label className="block space-y-2">
						<span className="block font-semibold">Embed height: {block.content.height}px</span>
						<input
							type="range"
							min={htmlEmbedMinHeight}
							max={htmlEmbedMaxHeight}
							step={10}
							value={block.content.height}
							onChange={event => update({ height: Number(event.target.value) })}
							className="w-full"
						/>
						<span className="block text-sm text-gray-500">
							Adjust the height to fit your content. Longer content scrolls inside the block.
						</span>
					</label>
					<button
						type="button"
						onClick={() => setPreviewHtml(block.content.html)}
						className="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
					>
						Preview
					</button>
				</div>
				<LandingHtmlEmbed content={{ html: previewHtml, height: block.content.height }} showPlaceholder />
			</section>
		</EditorShell>
	)
}
